import { type PropsWithChildren, createContext, useContext, useMemo } from 'react'
import { useIsAuthenticated } from '@refinedev/core'
import type { TAuthData } from '@modules/types'

interface CheckResponse extends TAuthData {
  roles?: string[]
}

type AuthDataType = TAuthData | null | undefined

interface AuthContextType {
  authLoading: boolean
  authData: AuthDataType
  permissionsLoading: boolean
  permissions: string[] | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const { isLoading: authLoading, data: authData } = useIsAuthenticated()

  const permissions = authData?.authenticated ? (authData as CheckResponse).roles || [] : null

  const permissionsLoading = authLoading

  const value: AuthContextType = useMemo(
    () => ({
      authLoading,
      authData,
      permissionsLoading,
      permissions,
    }),
    [authLoading, authData, permissionsLoading, permissions],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
